import { z } from "zod";

import { OpaqueIdSchema } from "./identity-schemas";

export const AuditOutcomeSchema = z.enum(["success", "denied", "error"]);
export const AuditPayloadSchema = z
  .record(z.string().min(1).max(80), z.union([z.string(), z.number(), z.boolean(), z.null()]))
  .refine(
    (value) => JSON.stringify(value).length <= 2048,
    "Audit payload projection exceeds 2,048 characters.",
  );

export const AuditInputSchema = z.strictObject({
  id: OpaqueIdSchema,
  event: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z][a-z0-9_.-]*$/),
  requestId: OpaqueIdSchema,
  actorUserId: OpaqueIdSchema.optional(),
  organizationId: OpaqueIdSchema.optional(),
  appInstanceId: OpaqueIdSchema.optional(),
  subjectKey: z.string().max(128).optional(),
  payload: AuditPayloadSchema.optional(),
  outcome: AuditOutcomeSchema,
  createdAt: z.number().int().positive().max(253402300799),
});
export type AuditInput = z.infer<typeof AuditInputSchema>;

export const AuditStoredEventSchema = z.strictObject({
  id: OpaqueIdSchema,
  event: z.string().min(1).max(100),
  request_id: OpaqueIdSchema,
  actor_user_id: OpaqueIdSchema.nullable(),
  organization_id: OpaqueIdSchema.nullable(),
  app_instance_id: OpaqueIdSchema.nullable(),
  subject_key: z.string().max(128).nullable(),
  payload_json: z
    .string()
    .max(2048)
    .refine((value) => {
      try {
        return AuditPayloadSchema.safeParse(JSON.parse(value)).success;
      } catch {
        return false;
      }
    }),
  outcome: AuditOutcomeSchema,
  created_at: z.number().int().positive().max(253402300799),
});
export type AuditStoredEvent = z.infer<typeof AuditStoredEventSchema>;

export const AuditPageSizeSchema = z.union([z.literal(10), z.literal(25), z.literal(50)]);
export const AuditActivityActionSchema = z.enum([
  "create",
  "read",
  "update",
  "delete",
  "execute",
  "open",
]);
export const AuditQuerySchema = z
  .strictObject({
    organizationId: OpaqueIdSchema.optional(),
    view: z.enum(["activity", "all"]).optional(),
    application: z.string().trim().min(1).max(120).optional(),
    person: z.string().trim().min(1).max(120).optional(),
    record: z.string().trim().min(1).max(128).optional(),
    action: AuditActivityActionSchema.optional(),
    q: z.string().trim().min(1).max(100).optional(),
    outcome: AuditOutcomeSchema.optional(),
    from: z.coerce.number().int().nonnegative().max(253402300799).optional(),
    to: z.coerce.number().int().positive().max(253402300799).optional(),
    pageSize: z.coerce.number().pipe(AuditPageSizeSchema).default(25),
    cursor: z
      .string()
      .min(1)
      .max(1024)
      .regex(/^[A-Za-z0-9_-]+$/)
      .optional(),
  })
  .refine(
    (value) => value.from === undefined || value.to === undefined || value.from <= value.to,
    "The start date must be before the end date.",
  );
export type AuditQuery = z.infer<typeof AuditQuerySchema>;

export const AuditEventSchema = z.strictObject({
  id: OpaqueIdSchema,
  event: z.string().min(1).max(100),
  requestId: OpaqueIdSchema,
  actorUserId: OpaqueIdSchema.nullable(),
  actorDisplayName: z.string().min(1).max(120).nullable(),
  actorUsername: z.string().max(320).nullable(),
  organizationId: OpaqueIdSchema.nullable(),
  customerName: z.string().max(160).nullable(),
  appInstanceId: OpaqueIdSchema.nullable(),
  appName: z.string().min(1).max(120).optional(),
  subjectKey: z.string().max(128).nullable(),
  outcome: AuditOutcomeSchema,
  payload: AuditPayloadSchema,
  createdAt: z.number().int().positive(),
});
export type AuditEvent = z.infer<typeof AuditEventSchema>;
export const AuditResponseSchema = z.strictObject({
  events: z.array(AuditEventSchema).max(50),
  nextCursor: z.string().max(1024).nullable(),
  pageSize: AuditPageSizeSchema,
  from: z.number().int().nonnegative(),
  to: z.number().int().positive(),
  checkedAt: z.number().int().positive(),
});
export type AuditResponse = z.infer<typeof AuditResponseSchema>;
